import { ColumnDimension } from "@components/common/data-grid/adm-data-grid-types";
import {
  PLANS_TABLE_COLUMN_LABELS,
  PlansTableColumnField,
  PlansTableGridColDef,
} from "@components/plans-table/plans-table-types";
import { formatPlanTableName } from "@utils/create-plan-utils";
import { getPlanAssigneeName } from "@utils/data-display";
import {
  PlanAssignedToCell,
  PlanAssigneeNameCell,
  PlanExpirationDateCell,
  PlanLimitCell,
  PlanNameCell,
  PlanOptionsCell,
  PlanStartDateCell,
  PlanStatusCell,
} from "@utils/data-grid-utils/plans-data-grid-cells";
import {
  formatCurrentAndMaxValueLimit,
  UNLIMITED,
} from "@utils/limit-data-display";
import { useMemo } from "react";
import { sortByPlanStatus } from "@components/plans-table/custom-sorting";
import {
  planColumnFilterOperators,
  statusColumnFilterOperators,
} from "@components/plans-table/plans-table-filter";

const columnDimension: ColumnDimension<PlansTableColumnField> = {
  plan: {
    flex: 1.5,
  },
  limits: {
    flex: 1,
  },
  status: {
    flex: 1.0,
  },
  assignedTo: {
    flex: 1,
  },
  assigneeName: {
    flex: 1.5,
  },
  startDate: {
    flex: 1,
  },
  expirationDate: {
    flex: 1,
  },
  options: {
    flex: 1,
    maxWidth: 120,
  },
};

/**
 * @returns the columns definition to be used in the plans table
 */
export function usePlansTableColumns(): PlansTableGridColDef[] {
  const columnsMap: Record<PlansTableColumnField, PlansTableGridColDef> =
    useMemo(() => {
      return {
        plan: {
          field: "plan",
          headerName: PLANS_TABLE_COLUMN_LABELS.plan,
          ...columnDimension.plan,
          valueGetter: (_, row) => formatPlanTableName(row.entity),
          renderCell: PlanNameCell,
          // Needed by the dev mode filters, which classify a plan by its features and bundles
          filterOperators: planColumnFilterOperators,
        },

        limits: {
          field: "limits",
          headerName: PLANS_TABLE_COLUMN_LABELS.limits,
          ...columnDimension.limits,
          valueGetter: (_, row) => {
            const formattedLimits = row.entity.constraints.map(
              formatCurrentAndMaxValueLimit
            );
            return formattedLimits.length > 0
              ? formattedLimits.join(", ")
              : UNLIMITED;
          },
          renderCell: PlanLimitCell,
        },

        status: {
          field: "status",
          headerName: PLANS_TABLE_COLUMN_LABELS.status,
          ...columnDimension.status,
          valueGetter: (_, row) => row.entity.status.identifier,
          sortComparator: sortByPlanStatus,
          renderCell: PlanStatusCell,
          // Needed by the Status dropdown, which matches a plan against any of the selected statuses
          filterOperators: statusColumnFilterOperators,
        },

        assignedTo: {
          field: "assignedTo",
          headerName: PLANS_TABLE_COLUMN_LABELS.assignedTo,
          ...columnDimension.assignedTo,
          valueGetter: (_, row) => row.entity.subject.type,
          renderCell: PlanAssignedToCell,
        },

        assigneeName: {
          field: "assigneeName",
          headerName: PLANS_TABLE_COLUMN_LABELS.assigneeName,
          ...columnDimension.assigneeName,
          valueGetter: (_, row) => getPlanAssigneeName(row.entity),
          renderCell: PlanAssigneeNameCell,
        },

        startDate: {
          field: "startDate",
          headerName: PLANS_TABLE_COLUMN_LABELS.startDate,
          ...columnDimension.startDate,
          valueGetter: (_, row) => row.entity.startDate,
          renderCell: PlanStartDateCell,
        },

        expirationDate: {
          field: "expirationDate",
          headerName: PLANS_TABLE_COLUMN_LABELS.expirationDate,
          ...columnDimension.expirationDate,
          valueGetter: (_, row) => row.entity.endDate,
          renderCell: PlanExpirationDateCell,
        },

        options: {
          field: "options",
          type: "actions",
          headerName: PLANS_TABLE_COLUMN_LABELS.options,
          ...columnDimension.options,
          getApplyQuickFilterFn: () => null,
          align: "right",
          renderCell: PlanOptionsCell,
        },
      };
    }, []);

  return Object.values(columnsMap);
}
